import { useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useWishlistStore } from "@/stores/wishlistStore";
import { supabase } from "@/integrations/supabase/client";

export const useWishlistSync = () => {
  const { user, loading: authLoading } = useAuth();
  const { items, setItems, setDatabaseCallbacks } = useWishlistStore();
  const initialSyncDone = useRef(false);
  const userIdRef = useRef<string | null>(null);

  // Store user ID in ref to avoid recreating callbacks
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user?.id]);

  // Set database callbacks once on mount (they use ref internally)
  useEffect(() => {
    const addToDatabase = async (modelId: string) => {
      const userId = userIdRef.current;
      if (!userId) return;

      try {
        await supabase.from("wishlists").upsert({
          user_id: userId,
          model_id: modelId,
        }, {
          onConflict: "user_id,model_id",
          ignoreDuplicates: true,
        });
      } catch (error) {
        console.error("Error adding to wishlist database:", error);
      }
    };

    const removeFromDatabase = async (modelId: string) => {
      const userId = userIdRef.current;
      if (!userId) return;

      try {
        await supabase
          .from("wishlists")
          .delete()
          .eq("user_id", userId)
          .eq("model_id", modelId);
      } catch (error) {
        console.error("Error removing from wishlist database:", error);
      }
    };

    setDatabaseCallbacks(addToDatabase, removeFromDatabase);

    return () => {
      setDatabaseCallbacks(null, null);
    };
  }, [setDatabaseCallbacks]);

  // Sync local wishlist to database when user logs in
  const syncLocalToDatabase = useCallback(async (userId: string, currentItems: typeof items) => {
    if (currentItems.length === 0) return;

    try {
      // Get model IDs from slugs
      const slugs = currentItems.map((item) => item.slug);
      const { data: models } = await supabase
        .from("models")
        .select("id, slug")
        .in("slug", slugs);

      if (!models || models.length === 0) return;

      // Insert wishlist items (ignore conflicts)
      const wishlistItems = models.map((model) => ({
        user_id: userId,
        model_id: model.id,
      }));

      await supabase.from("wishlists").upsert(wishlistItems, {
        onConflict: "user_id,model_id",
        ignoreDuplicates: true,
      });
    } catch (error) {
      console.error("Error syncing local wishlist to database:", error);
    }
  }, []);

  // Load wishlist from database
  const loadFromDatabase = useCallback(async (userId: string) => {
    try {
      const { data: wishlistItems, error } = await supabase
        .from("wishlists")
        .select(`
          model_id,
          models (
            id,
            slug,
            name,
            image_url,
            price
          )
        `)
        .eq("user_id", userId);

      if (error) throw error;

      if (wishlistItems && wishlistItems.length > 0) {
        const formattedItems = wishlistItems
          .filter((item) => item.models)
          .map((item) => ({
            id: (item.models as any).id,
            slug: (item.models as any).slug,
            name: (item.models as any).name,
            image_url: (item.models as any).image_url,
            price: (item.models as any).price,
          }));

        setItems(formattedItems);
      }
    } catch (error) {
      console.error("Error loading wishlist from database:", error);
    }
  }, [setItems]);

  // Sync on login/logout
  useEffect(() => {
    if (authLoading) return;

    if (user && !initialSyncDone.current) {
      // User logged in - sync local items then load from database
      initialSyncDone.current = true;
      const currentItems = useWishlistStore.getState().items;
      syncLocalToDatabase(user.id, currentItems).then(() => loadFromDatabase(user.id));
    } else if (!user) {
      initialSyncDone.current = false;
    }
  }, [user, authLoading, syncLocalToDatabase, loadFromDatabase]);

  return {
    isLoggedIn: !!user,
  };
};
